import {
  ArrowDownIcon,
  ArrowPathIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  EyeSlashIcon,
  MicrophoneIcon,
  SparklesIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorState } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import axios from "axios";
import cn from "classnames";
import toast from "react-hot-toast";
import {
  abortCaseUpload,
  dismissCaseToast,
} from "../services/audioProcessingService";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAuth } from "../context/AuthContext";
import {
  AudioMetadataType,
  CaseScore,
  CaseStatus,
  CaseStatusType,
  ChecklistData,
  ChecklistTemplate,
  PendingSuggestionsChanges,
  PreferenceStatuteStateType,
  RedactionType,
  StatuteType,
  PolicyReference,
  Suggestion,
} from "../db/schema";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUserRoles } from "../utilities/roleUtils";
import {
  isWhisperFormat,
  isTranscribeFormat,
} from "../utils/transcriptionHelpers";
import ChecklistView from "./Checklist/ChecklistView";
import Button from "./common/Button";
import MediaPlayer from "./common/MediaPlayer";
import EditorMenuBar from "./Editor/EditorMenuBar";
import FloatingMenu from "./Editor/FloatingMenu";
import IndentHandler from "./Editor/IndentHandler";
import ReportScore from "./Editor/ReportScore";
import { SearchAndReplace } from "./Editor/SearchAndReplace";
import { ReplaceText } from "./Editor/ReplaceText";
import { Redactions as RedactionsExtension } from "./Editor/RedactionsExtension";
import { Proofread } from "./Editor/ProofreadExtension";
import ProofreadPopover from "./Editor/ProofreadPopover";
import { useProofreader } from "../hooks/useProofreader";
import { useTypingSpeed } from "../hooks/useTypingSpeed";
import Checklists from "./Editor/Checklists";
import Redactions from "./Editor/Redactions";
import Snippets from "./Editor/Snippets";
import Statutes from "./Editor/Statutes";
import PolicySectionView from "./Editor/PolicySectionView";
import { resolvePolicyLabel } from "./Policies/api";
import Suggestions from "./Editor/Suggestions";
import Transcription from "./Editor/Transcription";
import FootagePanel from "./Editor/FootagePanel";
import FootageProgress from "./Editor/FootageProgress";
import { videoApi } from "../services/video/api";
import ResizeHandle from "./common/ResizeHandle";
import { useResizablePanel } from "../hooks/useResizablePanel";
import { EDITOR_NEW_CASE_RESET_EVENT } from "../utilities/editorNewCaseReset";

interface TiptapEditorProps {
  onChange: (data: {
    content: { data: string };
    caseId?: string;
    statutes?: StatuteType[];
  }) => void;
  caseId: string | undefined;
  initialContent: string;
  caseSuggestions?: Suggestion[];
  caseScore?: CaseScore;
  caseAudioUrl?: string;
  isReadOnly?: boolean;
  caseStatutes?: StatuteType[];
  setCaseStatutes?: React.Dispatch<React.SetStateAction<StatuteType[]>>;
  caseRedactions?: RedactionType[];
  setCaseRedactions?: React.Dispatch<React.SetStateAction<RedactionType[]>>;
  casePolicyReferences?: PolicyReference[];
  isCaseLoading?: boolean;
  setCaseSuggestions?: React.Dispatch<React.SetStateAction<Suggestion[]>>;
  setCaseScore: React.Dispatch<React.SetStateAction<CaseScore | undefined>>;
  caseTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setCaseTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  supplementalTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setSupplementalTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  activeTab: "narrative" | "transcription";
  setActiveTab: React.Dispatch<
    React.SetStateAction<"narrative" | "transcription">
  >;
  caseAudioMetadata?: AudioMetadataType | null;
  setCaseAudioMetadata?: React.Dispatch<
    React.SetStateAction<AudioMetadataType | null>
  >;
  pendingSuggestionsChanges?: PendingSuggestionsChanges | null;
  onPendingChangesCleared?: () => void;
  isProcessing?: boolean;
  handleSelectedCase: (caseId: string) => void;
  canEditNarrative?: boolean;
  caseOwnerId?: string;
  setAudioControlsRef?: React.Dispatch<
    React.SetStateAction<{
      rewind: (seconds: number) => void;
      fastForward: (seconds: number) => void;
      play: () => void;
      pause: () => void;
    } | null>
  >;
  checklist: {
    template: ChecklistTemplate;
    data: ChecklistData;
    completed_at?: string;
    created_at?: string;
    updated_at?: string;
  } | null;
  caseFiles?: any[];
  setCaseFiles?: React.Dispatch<React.SetStateAction<any[]>>;
  caseStatus?: (typeof CaseStatus.enumValues)[number];
  isAiGenerated?: boolean;
  setIsAiGenerated?: React.Dispatch<React.SetStateAction<boolean>>;
  onCaseDeleted?: (caseId: string) => void;
}

function resetEditorContent(editor: any, newContent: string) {
  editor.commands.setContent(newContent);

  // Clear the history by creating a new state
  const newEditorState = EditorState.create({
    doc: editor.state.doc,
    plugins: editor.state.plugins,
    schema: editor.state.schema,
  });
  editor.view.updateState(newEditorState);
}

const SkeletonEditorContent = () => (
  <div className="mt-8">
    <Skeleton className="mb-8" height={40} width="80%" />
    {[...Array(6)].map((_, index) => (
      <Skeleton key={index} height={16} width={`${Math.random() * 40 + 60}%`} />
    ))}
    <div className="mt-5" />
    {[...Array(6)].map((_, index) => (
      <Skeleton key={index} height={16} width={`${Math.random() * 40 + 60}%`} />
    ))}
    <div className="mt-5" />
    {[...Array(6)].map((_, index) => (
      <Skeleton key={index} height={16} width={`${Math.random() * 40 + 60}%`} />
    ))}
  </div>
);

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  onChange,
  caseId,
  initialContent,
  caseSuggestions,
  caseScore,
  caseAudioUrl,
  isReadOnly,
  caseStatutes,
  setCaseStatutes,
  caseRedactions,
  setCaseRedactions,
  casePolicyReferences,
  isCaseLoading,
  setCaseSuggestions,
  setCaseScore,
  setCaseTranscription,
  caseTranscription,
  supplementalTranscription,
  setSupplementalTranscription,
  setActiveTab,
  activeTab,
  caseAudioMetadata,
  setCaseAudioMetadata,
  pendingSuggestionsChanges,
  onPendingChangesCleared,
  isProcessing,
  handleSelectedCase,
  canEditNarrative,
  caseOwnerId,
  setAudioControlsRef,
  checklist,
  caseFiles,
  setCaseFiles,
  caseStatus,
  isAiGenerated,
  setIsAiGenerated,
  onCaseDeleted,
}) => {
  const { isRecords } = useUserRoles();
  const { user, reportPreferences } = useAuth();
  const isMobile = useIsMobile();
  const checklistDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check if this is a Records user viewing someone else's case
  const isRecordsViewingOtherCase =
    isRecords() && caseOwnerId && caseOwnerId !== user?.id;

  const [minimized, setMinimized] = useState<boolean>(() => {
    const saved = localStorage.getItem("pnai-editorMinimized");
    return saved ? JSON.parse(saved) : isMobile;
  });
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isRegeneratingNarrative, setIsRegeneratingNarrative] = useState(false);
  const [isAddingToReport, setIsAddingToReport] = useState(false);
  const [isCancellingAddToReport, setIsCancellingAddToReport] = useState(false);
  // True from the moment the user clicks Add to Report until
  // audioProcessingService finishes the local S3 upload phase. While true,
  // the overlay tells the user to KEEP the page open. Once flipped off (via
  // the pnai:add-to-report-upload-complete window event), the message
  // switches to "you can safely leave this page". A page refresh during
  // processing leaves this false (the local upload pipeline isn't running
  // anymore), so the overlay correctly shows the "safely leave" message.
  const [isUploadingAddToReport, setIsUploadingAddToReport] = useState(false);
  // Persisted "Add to Report is in flight" signal — derived from the case's
  // audioMetadata.intent so the "Generating changes" overlay survives a page
  // refresh. The intent is set to "add_to_report" while files are being uploaded
  // and transitions to "generating_changes" once the completion handler claims
  // the batch and starts running Bedrock. Both sub-states should show the
  // overlay; the intent is cleared (and pendingSuggestionsChanges populated)
  // when Bedrock returns or when the user cancels.
  const persistedIntent = (caseAudioMetadata as any)?.intent;
  const isAddToReportInFlight =
    (persistedIntent === "add_to_report" ||
      persistedIntent === "generating_changes") &&
    !pendingSuggestionsChanges;
  const showAddToReportOverlay = isAddingToReport || isAddToReportInFlight;
  // When an Add-to-Report kicks off locally, optimistically reflect the
  // in-flight intent into caseAudioMetadata. This bridges the local kickoff to
  // the polling loop in CasesLayout (which watches audioMetadata.intent), so it
  // starts polling for the persisted changes immediately — even if the case has
  // been open a while — and the review dialog surfaces without a manual refresh.
  // The next server refresh overwrites this with the authoritative value.
  useEffect(() => {
    if (!isAddingToReport || pendingSuggestionsChanges) return;
    const intent = (caseAudioMetadata as any)?.intent;
    if (intent === "add_to_report" || intent === "generating_changes") return;
    setCaseAudioMetadata?.(
      (prev) => ({ ...((prev as any) || {}), intent: "add_to_report" }) as any,
    );
  }, [
    isAddingToReport,
    pendingSuggestionsChanges,
    caseAudioMetadata,
    setCaseAudioMetadata,
  ]);
  // Locks the editor (no typing) while the user walks through pending Add-to-Report
  // changes. Lifted up here so the canonical editable effect below stays the
  // single source of truth — SuggestionsReview just flips this flag instead of
  // toggling editor.setEditable directly (which used to leave windows where
  // typing slipped through between commands).
  const [isReviewingChanges, setIsReviewingChanges] = useState(false);
  const [activeTranscriptionType, setActiveTranscriptionType] =
    useState<string>("main");
  const [showTranscriptionDropdown, setShowTranscriptionDropdown] =
    useState(false);
  const [showNarrativeDropdown, setShowNarrativeDropdown] = useState(false);
  const [fileTranscriptions, setFileTranscriptions] = useState<
    Record<string, any>
  >({});
  const [isLoadingTranscription, setIsLoadingTranscription] = useState(false);
  // Slot under the editor toolbar that the transcript's find bar portals into
  const [findSlot, setFindSlot] = useState<HTMLDivElement | null>(null);
  const [transcriptFindOpen, setTranscriptFindOpen] = useState(false);
  const confirmDialogRef = useRef<HTMLDialogElement>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  // Reset transcription state when caseId changes — clears stale file selection
  // before the new case's files arrive from the API. The auto-select effect
  // below guards against firing fetches with files from a previous case.
  useEffect(() => {
    setFileTranscriptions({});
    setActiveTranscriptionType("main");
    setCurrentTime(0);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = 0;
      audioPlayerRef.current.pause();
    }
  }, [caseId]);

  // Memoize the onWordClick callback to prevent unnecessary re-renders of Transcription
  const handleWordClick = useCallback(
    (time: number, expand?: boolean) => {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.currentTime = time;
        audioPlayerRef.current.play();
        if (expand && minimized) {
          setMinimized(false);
          localStorage.setItem("pnai-editorMinimized", "false");
        }
      }
    },
    [minimized],
  );

  // Get list of case files with transcriptions
  const filesWithTranscriptions = useMemo(() => {
    return (caseFiles || []).filter((file) => file.metadata?.transcriptionUrl);
  }, [caseFiles]);
  // Files whose footage was analyzed (or is being analyzed): the Footage tab.
  const footageFiles = useMemo(() => (caseFiles || []).filter((file) => file.metadata?.video), [caseFiles]);
  // With footage, the Transcription tab becomes "Footage" and opens on the
  // video view; a toggle inside it switches to the editable transcription.
  const [footageView, setFootageView] = useState<"footage" | "transcription">("footage");
  useEffect(() => {
    setFootageView("footage");
  }, [caseId]);
  const hasFootage = footageFiles.length > 0 && Boolean(caseId);
  const showFootage = hasFootage && activeTab === "transcription" && footageView === "footage";

  useEffect(() => {
    if (activeTab !== "transcription" || showFootage) setTranscriptFindOpen(false);
  }, [activeTab, showFootage]);
  // Presigned proxy videos for the sidebar mini player, one per footage
  // file (fetched once the frames Lambda has written the proxy).
  const [proxyUrls, setProxyUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    setProxyUrls({});
  }, [caseId]);
  useEffect(() => {
    if (!caseId) return;
    for (const f of footageFiles) {
      if (!f.metadata?.video?.proxyKey || proxyUrls[f.id]) continue;
      videoApi
        .proxyUrl(caseId, f.id)
        .then((url) => setProxyUrls((prev) => (prev[f.id] ? prev : { ...prev, [f.id]: url })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, footageFiles]);
  // Which footage file the sidebar shows: the active transcription's file,
  // or the case's only footage file.
  const sidebarFootageFile = useMemo(() => {
    if (!hasFootage) return null;
    const byId = footageFiles.find((f) => f.id === activeTranscriptionType);
    if (byId) return byId;
    return footageFiles.length === 1 ? footageFiles[0] : null;
  }, [hasFootage, footageFiles, activeTranscriptionType]);
  const sidebarVideoUrl = sidebarFootageFile ? proxyUrls[sidebarFootageFile.id] : undefined;
  // The footage view's playhead; the sidebar resumes from it, and the footage
  // view opens where the sidebar was.
  const footageTime = useRef<{ fileId: string; ms: number } | null>(null);
  const [footageOpenAtMs, setFootageOpenAtMs] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (showFootage) {
      // Collapsing the sidebar player: pause it and hand its position to the footage view.
      const el = audioPlayerRef.current;
      if (el) {
        setFootageOpenAtMs(Math.round(el.currentTime * 1000));
        el.pause();
      }
    } else if (footageTime.current && audioPlayerRef.current && sidebarFootageFile?.id === footageTime.current.fileId) {
      audioPlayerRef.current.currentTime = footageTime.current.ms / 1000;
      setCurrentTime(footageTime.current.ms / 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFootage]);

  // Get the current transcription and audio URL based on active type
  const currentTranscription = useMemo(() => {
    if (activeTranscriptionType === "main") {
      return caseTranscription;
    } else if (activeTranscriptionType === "supplemental") {
      return supplementalTranscription;
    } else {
      // It's a file ID
      return fileTranscriptions[activeTranscriptionType] || null;
    }
  }, [
    activeTranscriptionType,
    caseTranscription,
    supplementalTranscription,
    fileTranscriptions,
  ]);

  const currentAudioUrl = useMemo(() => {
    console.log("Computing currentAudioUrl for type:", activeTranscriptionType);
    console.log("caseAudioUrl:", caseAudioUrl);
    console.log("caseAudioMetadata:", caseAudioMetadata);
    console.log("supplementalAudio:", caseAudioMetadata?.supplementalAudio);

    if (activeTranscriptionType === "main") {
      return caseAudioUrl;
    } else if (activeTranscriptionType === "supplemental") {
      const supplementalUrl =
        caseAudioMetadata?.supplementalAudio?.audioUrl || "";
      console.log("Supplemental audio URL:", supplementalUrl);
      return supplementalUrl;
    } else {
      // It's a file ID - find the file and return its audio URL
      const file = filesWithTranscriptions.find(
        (f) => f.id === activeTranscriptionType,
      );
      return file?.file_url || "";
    }
  }, [
    activeTranscriptionType,
    caseAudioUrl,
    caseAudioMetadata,
    filesWithTranscriptions,
  ]);

  // Get the current audio metadata based on active type
  const currentAudioMetadata = useMemo(() => {
    if (activeTranscriptionType === "main") {
      return caseAudioMetadata;
    } else if (activeTranscriptionType === "supplemental") {
      // Create metadata object for supplemental audio
      return {
        ...caseAudioMetadata,
        // Override with supplemental audio properties, providing fallbacks
        audioUrl: caseAudioMetadata?.supplementalAudio?.audioUrl,
        fileSize: caseAudioMetadata?.supplementalAudio?.fileSize,
        fileType: caseAudioMetadata?.supplementalAudio?.fileType,
        audioType: caseAudioMetadata?.supplementalAudio?.type || "narrative",
      } as AudioMetadataType;
    } else {
      // It's a file ID - use file metadata
      const file = filesWithTranscriptions.find(
        (f) => f.id === activeTranscriptionType,
      );
      return file?.metadata || null;
    }
  }, [activeTranscriptionType, caseAudioMetadata, filesWithTranscriptions]);

  // Check if we have multiple transcriptions and calculate total count
  const transcriptionCount = useMemo(() => {
    let count = 0;
    if (caseTranscription) count++;
    if (supplementalTranscription) count++;
    count += filesWithTranscriptions.length;
    return count;
  }, [caseTranscription, supplementalTranscription, filesWithTranscriptions]);

  const hasMultipleTranscriptions = useMemo(() => {
    return transcriptionCount > 1;
  }, [transcriptionCount]);

  // Handle transcription type switching
  const handleTranscriptionTypeChange = useCallback(
    async (type: string) => {
      setActiveTranscriptionType(type);
      setShowTranscriptionDropdown(false);

      // If it's a file ID and we haven't fetched its transcription yet, fetch it
      if (
        type !== "main" &&
        type !== "supplemental" &&
        !fileTranscriptions[type]
      ) {
        const file = filesWithTranscriptions.find((f) => f.id === type);
        if (file?.metadata?.transcriptionUrl && caseId) {
          setIsLoadingTranscription(true);
          try {
            // Use server endpoint for caching instead of direct S3 URL
            const response = await axios.get(
              `/api/cases/${caseId}/files/${file.id}/transcription`,
              { withCredentials: true },
            );
            setFileTranscriptions((prev) => ({
              ...prev,
              [type]: response.data,
            }));
          } catch (error) {
            console.error(
              "Failed to fetch file transcription from primary endpoint, falling back to by-url:",
              error,
            );
            // Fallback: server fetches by URL (covers 404 timing issues
            // where the case_files row hasn't been written yet). Direct
            // browser → S3 fetch no longer works because the bucket is private.
            try {
              const fallbackResponse = await axios.post(
                `/api/cases/transcription-by-url`,
                { url: file.metadata.transcriptionUrl },
                { withCredentials: true },
              );
              setFileTranscriptions((prev) => ({
                ...prev,
                [type]: fallbackResponse.data,
              }));
            } catch (fallbackError) {
              console.error(
                "Failed to fetch file transcription via fallback:",
                fallbackError,
              );
            }
          } finally {
            setIsLoadingTranscription(false);
          }
        }
      }

      // Reset audio position and highlighting when switching transcriptions
      setCurrentTime(0);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.currentTime = 0;
        audioPlayerRef.current.pause();
      }
    },
    [filesWithTranscriptions, fileTranscriptions, caseId],
  );

  // Handle regenerate narrative from dropdown
  const handleRegenerateNarrative = useCallback(async () => {
    // Use currentTranscription which accounts for multi-file cases
    const transcription = currentTranscription;

    if (!transcription || !caseId) {
      toast.error("No transcription or case ID available", { duration: 5000 });
      return;
    }

    setIsRegeneratingNarrative(true);
    confirmDialogRef.current?.close();
    setShowNarrativeDropdown(false);

    try {
      // For AWS Transcribe format, only send audio_segments to reduce payload size
      let transcriptionPayload:
        | typeof transcription
        | {
            results: {
              audio_segments: Array<{
                id: number;
                transcript: string;
                start_time: string;
                end_time: string;
                speaker_label: string;
                items: number[];
              }>;
            };
          }
        | {
            text: string;
          } = transcription;

      if (isTranscribeFormat(transcription)) {
        transcriptionPayload = {
          results: {
            audio_segments: transcription.results.audio_segments || [],
          },
        };
      }

      if (isWhisperFormat(transcription)) {
        transcriptionPayload = {
          text: transcription.text,
        };
      }

      const response = await axios.post(
        "/api/regenerate-narrative",
        {
          caseId,
          transcription: transcriptionPayload,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success) {
        toast.success("Narrative regeneration started.", {
          duration: 5000,
        });

        handleSelectedCase(caseId);
      }
    } catch (error) {
      console.error("Error regenerating narrative:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Error regenerating narrative"
        : "An unexpected error occurred";

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsRegeneratingNarrative(false);
    }
  }, [currentTranscription, caseId, handleSelectedCase]);

  // Reset to main transcription when switching to a case without supplemental
  useEffect(() => {
    if (
      activeTranscriptionType === "supplemental" &&
      !supplementalTranscription
    ) {
      setActiveTranscriptionType("main");
      // Reset audio position and highlighting when switching back to main
      setCurrentTime(0);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.currentTime = 0;
        audioPlayerRef.current.pause();
      }
    }
  }, [activeTranscriptionType, supplementalTranscription]);

  // Set initial transcription type to first available when main transcription is not available
  // This handles multi-file cases where transcriptions are stored in case_files
  useEffect(() => {
    if (activeTranscriptionType === "main" && !caseTranscription) {
      // No main transcription, find the first available
      if (supplementalTranscription) {
        setActiveTranscriptionType("supplemental");
      } else if (filesWithTranscriptions.length > 0) {
        // Auto-select the first file with transcription and fetch it.
        // Guard against firing a fetch for a file from a previous case
        // (caseFiles can lag caseId during navigation).
        const firstFile = filesWithTranscriptions[0];
        if (firstFile.case_id === caseId) {
          handleTranscriptionTypeChange(firstFile.id);
        }
      }
    }
  }, [
    caseTranscription,
    supplementalTranscription,
    filesWithTranscriptions,
    activeTranscriptionType,
    handleTranscriptionTypeChange,
    caseId,
  ]);

  // Close dropdown when clicking outside - proper approach
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Only close if clicking outside the dropdown area
      if (!target.closest(".transcription-dropdown-container")) {
        setShowTranscriptionDropdown(false);
      }
    };

    if (showTranscriptionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showTranscriptionDropdown]);

  // Close narrative dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".narrative-dropdown-container")) {
        setShowNarrativeDropdown(false);
      }
    };

    if (showNarrativeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showNarrativeDropdown]);

  useEffect(() => {
    if (isMobile) {
      setMinimized(true);
      localStorage.setItem("pnai-editorMinimized", JSON.stringify(true));
    }
  }, [isMobile]);

  // On smaller viewports the sidebar nav collapses into a dropdown; this tracks
  // whether it's expanded. Lifted out of SidebarNav (an inline component that
  // remounts on every parent render) so it doesn't snap shut unexpectedly.
  const [isSidebarNavOpen, setIsSidebarNavOpen] = useState(false);

  // Actual rendered width of the tool sidebar. The sidebar is a fraction of the
  // editor column, so browser zoom (many officers run at 150%) shrinks it well
  // below what the tool labels need — at 150% it lands around 176px, where
  // "Suggestions"/"Redactions" ellipse down to "Su..."/"Re...". We measure
  // rather than key off a viewport breakpoint because the same panel also
  // narrows when the window is resized or the case list is open.
  const [sidebarWidth, setSidebarWidth] = useState(0);
  // User-chosen sidebar width (desktop only), persisted to localStorage.
  const sidebarPanel = useResizablePanel({
    storageKey: "pnai-editorSidebarWidth",
    defaultWidth: 360,
    minWidth: 220,
    maxWidth: 720,
    side: "left",
    maxViewportFraction: 0.5,
  });
  const sidebarObserverRef = useRef<ResizeObserver | null>(null);
  // Callback ref, not useRef + useEffect([]): the sidebar node is remounted
  // when the panel collapses/expands, and a mount-only effect would keep
  // observing the detached original and never report a new width again.
  const sidebarRef = useCallback((node: HTMLDivElement | null) => {
    sidebarObserverRef.current?.disconnect();
    sidebarObserverRef.current = null;
    if (!node) return;
    // Seed synchronously. reportWebVitals patches ResizeObserver to defer every
    // callback through requestAnimationFrame, so the first measurement would
    // otherwise be a frame late (and never arrive at all in a background tab).
    setSidebarWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      setSidebarWidth(entry.contentRect.width);
    });
    observer.observe(node);
    sidebarObserverRef.current = observer;
  }, []);

  // Below ~240px a two-column tool tile can't fit its label (the longest,
  // "Suggestions", needs ~60px of text room after the icon and padding), so the
  // nav drops to icons only and the panel's padding tightens. Nothing is lost:
  // each tool's panel renders its own heading directly underneath, and every
  // button keeps a tooltip + accessible name.
  const isSidebarCompact = sidebarWidth > 0 && sidebarWidth < 240;
  const [activeComponent, setActiveComponent] = useState<
    "suggestions" | "snippets" | "statutes" | "redactions" | "checklists"
  >(() => {
    const saved = localStorage.getItem("pnai-editorActiveComponent");
    // A Policies tab used to exist; anyone who left it open lands on Suggestions.
    if (saved === "policies") return "suggestions";
    return (
      (saved as
        | "suggestions"
        | "snippets"
        | "statutes"
        | "redactions"
        | "checklists") || "suggestions"
    );
  });
  // A policy section opened from a suggestion's source badge; shown in the
  // sidebar in place of the suggestions list until Back (or another tab).
  const [openPolicySection, setOpenPolicySection] = useState<PolicyReference | null>(null);
  useEffect(() => {
    setOpenPolicySection(null);
  }, [activeComponent, caseId]);
  const openPolicyFromBadge = async (label: string, reference?: PolicyReference) => {
    if (reference) return setOpenPolicySection(reference);
    try {
      const resolved = await resolvePolicyLabel(label);
      if (resolved) setOpenPolicySection(resolved);
      else toast.error("That policy section is no longer available");
    } catch {
      toast.error("Could not open that policy section");
    }
  };
  // A case just created from audio always opens on Suggestions; otherwise the
  // saved tab above wins.
  useEffect(() => {
    const reset = () => setActiveComponent("suggestions");
    window.addEventListener(EDITOR_NEW_CASE_RESET_EVENT, reset);
    return () => window.removeEventListener(EDITOR_NEW_CASE_RESET_EVENT, reset);
  }, []);
  const isUserEditing = useRef(false);
  const lastSavedContent = useRef(initialContent);

  // Switch to suggestions tab if navigating to a case without a checklist
  useEffect(() => {
    if (!checklist && activeComponent === "checklists") {
      setActiveComponent("suggestions");
      localStorage.setItem("pnai-editorActiveComponent", "suggestions");
    }
  }, [checklist, activeComponent]);

  // Auto-switch to the suggestions tab when an Add-to-Report batch returns
  // changes ready for review. The Suggestions component must be mounted for
  // its auto-open useEffect to fire and launch the review walkthrough.
  useEffect(() => {
    if (
      pendingSuggestionsChanges &&
      !pendingSuggestionsChanges.error &&
      pendingSuggestionsChanges.changes?.length &&
      activeComponent !== "suggestions"
    ) {
      setActiveComponent("suggestions");
      localStorage.setItem("pnai-editorActiveComponent", "suggestions");
    }
  }, [pendingSuggestionsChanges, activeComponent]);

  const extensions = useMemo(
    () => [
      Color,
      Highlight.configure({ multicolor: true }),
      IndentHandler,
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === "heading" && node.attrs.level === 1) {
            return "Case Title";
          }
          return "Start writing your case narrative here";
        },
      }),
      RedactionsExtension,
      Proofread,
      ReplaceText,
      SearchAndReplace.configure({
        searchResultClass: "search-result",
        disableRegex: true,
      }),
      StarterKit,
      TextStyle,
      Table.configure({
        resizable: true,
        lastColumnResizable: true,
        allowTableNodeSelection: true,
      }),
      TableRow.extend({
        content: "(tableHeader)*",
      }),
      TableHeader,
      TableCell,
      Underline,
    ],
    [],
  );

  const handleUpdate = useCallback(
    (editor) => {
      const html = editor.getHTML();
      if (html !== lastSavedContent.current) {
        // Don't update the editor content, just save it
        onChange({
          content: { data: html },
          caseId,
        });
        lastSavedContent.current = html;
      }
    },
    [onChange, caseId],
  );

  const handleUpdateStatutes = (statutes: StatuteType[]) => {
    if (onChange && caseId) {
      onChange({
        content: { data: editor?.getHTML() || "" },
        caseId,
        statutes,
      });
    }
  };

  const showDisclaimer =
    isAiGenerated &&
    reportPreferences?.statute_state ===
      PreferenceStatuteStateType.Values.California;

  const editor = useEditor({
    extensions,
    content: initialContent,
    editorProps: {
      attributes: {
        class: `prose dark:prose-invert max-w-none mt-8 leading-[1.5] max-w-full${!showDisclaimer ? " pb-32" : ""}`,
      },
    },
    onUpdate: ({ editor }) => {
      if (isUserEditing.current) {
        handleUpdate(editor);
      }
    },
    onFocus: () => {
      isUserEditing.current = true;
    },
    onBlur: () => {
      isUserEditing.current = false;
    },
  });

  useLayoutEffect(() => {
    if (editor) {
      if (!initialContent) {
        // Only reset if editor is completely empty
        if (editor.getHTML() === "") {
          editor.commands.clearContent();
          editor.commands.setNode("heading", { level: 1 });
        }
      } else if (
        initialContent !== editor.getHTML() &&
        !isUserEditing.current
      ) {
        // Only reset content if it's different AND user is not currently editing
        resetEditorContent(editor, initialContent);
        // Server-driven content is the new "untouched" baseline for the
        // proofreader; only what the officer types after this gets linted.
        editor.commands.setProofBaseline();
      }
      lastSavedContent.current = editor.getHTML();
    }
  }, [editor, initialContent]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isReadOnly && !isReviewingChanges);
    }
  }, [editor, isReadOnly, isReviewingChanges]);

  // Inline proofreading (Amazon Nova Micro via /api/proofread). The switch is
  // persisted like the redactions highlight so it follows the officer across
  // cases; it is deliberately not a report preference, which would surface
  // it on mobile where there is no proofreader.
  const [proofreadEnabled, setProofreadEnabled] = useState(() => {
    try {
      return localStorage.getItem("pnai-proofread") !== "false";
    } catch {
      return true;
    }
  });
  const toggleProofread = useCallback(() => {
    setProofreadEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("pnai-proofread", String(next));
      } catch {
        // localStorage unavailable (private mode); the toggle still works
        // for this session.
      }
      return next;
    });
  }, []);
  // "Hide placeholders": collapses the amber {{…}} chips in the editor and
  // strips them from every export, without touching the saved narrative, so
  // turning it back off restores each one. Persisted like the proofread switch.
  const [placeholdersHidden, setPlaceholdersHidden] = useState(() => {
    try {
      return localStorage.getItem("pnai-hidePlaceholders") === "true";
    } catch {
      return false;
    }
  });
  // Only a click animates; the sync on load (and when the editor is
  // recreated) applies the saved choice instantly.
  const animatePlaceholdersRef = useRef(false);
  const togglePlaceholdersHidden = useCallback(() => {
    animatePlaceholdersRef.current = true;
    setPlaceholdersHidden((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("pnai-hidePlaceholders", String(next));
      } catch {
        // localStorage unavailable (private mode); the toggle still works
        // for this session.
      }
      return next;
    });
  }, []);
  useEffect(() => {
    if (!editor) return;
    editor.commands.setPlaceholdersHidden(
      placeholdersHidden,
      animatePlaceholdersRef.current,
    );
    animatePlaceholdersRef.current = false;
  }, [editor, placeholdersHidden]);
  // Officer vocabulary the server tells the model to leave alone (name, rank,
  // call sign, Common Spellings). Changing it invalidates cached results.
  const proofreadWords = useMemo(() => {
    const raw: unknown[] = [
      reportPreferences?.name,
      reportPreferences?.rank,
      reportPreferences?.call_sign,
      ...(reportPreferences?.common_spellings || []),
    ];
    return raw
      .flatMap((v) => (typeof v === "string" ? v.split(/[\s,;/]+/) : []))
      .map((w) => w.replace(/^[^\w]+|[^\w.'-]+$/g, ""))
      .filter((w) => w.length > 1);
  }, [
    reportPreferences?.name,
    reportPreferences?.rank,
    reportPreferences?.call_sign,
    reportPreferences?.common_spellings,
  ]);
  useProofreader(editor, {
    enabled: proofreadEnabled && !isReadOnly && !isReviewingChanges,
    vocabularyKey: proofreadWords.join(" "),
  });
  // Typing speed (numbers only) for the staff usage dashboard's hours-saved estimate.
  useTypingSpeed(isReadOnly ? null : editor);

  // Listen for the audioProcessingService toast cancellation. When the user
  // clicks the X on the upload toast (instead of the overlay's Cancel
  // button), the service dispatches this event so the editor's "Generating
  // changes" overlay closes immediately and the case state is refreshed —
  // otherwise the user would have to wait up to 15s for the CasesLayout
  // poller to pick up the cleared intent.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { caseId?: string }
        | undefined;
      if (!detail?.caseId || detail.caseId !== caseId) return;
      setIsAddingToReport(false);
      handleSelectedCase(caseId);
    };
    window.addEventListener("pnai:add-to-report-cancelled", handler);
    return () =>
      window.removeEventListener("pnai:add-to-report-cancelled", handler);
  }, [caseId, handleSelectedCase]);

  // Listen for the audioProcessingService upload-phase completion event so
  // the overlay can switch its message from "keep this page open while
  // uploading" to "you can safely leave this page".
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { caseId?: string }
        | undefined;
      if (!detail?.caseId || detail.caseId !== caseId) return;
      setIsUploadingAddToReport(false);
    };
    window.addEventListener("pnai:add-to-report-upload-complete", handler);
    return () =>
      window.removeEventListener("pnai:add-to-report-upload-complete", handler);
  }, [caseId]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  useEffect(() => {
    setCaseTranscription(caseTranscription || null);
  }, [caseTranscription, setCaseTranscription]);

  // Push the case's redactions into the TipTap extension so its decoration
  // plugin can paint matches in the editor. Runs whenever the array reference
  // changes (new fetch, add custom, re-identify) AND once the editor mounts.
  useEffect(() => {
    if (!editor) return;
    editor.commands.setRedactions(caseRedactions || []);
  }, [editor, caseRedactions]);

  // Master "highlight redactions" switch. Lifted here so both the
  // Redactions sidebar toggle and the bubble-menu Redact/Unredact actions
  // share one source of truth (the bubble menu turns this on when used).
  // Persisted to localStorage so the preference follows the user across
  // cases (TiptapEditor remounts on caseId change and re-reads it here).
  const [redactionsHighlightEnabled, setRedactionsHighlightEnabled] = useState(
    () => {
      try {
        return (
          JSON.parse(
            localStorage.getItem("pnai-redactionsHighlight") || "false",
          ) === true
        );
      } catch {
        return false;
      }
    },
  );
  useEffect(() => {
    localStorage.setItem(
      "pnai-redactionsHighlight",
      JSON.stringify(redactionsHighlightEnabled),
    );
  }, [redactionsHighlightEnabled]);
  // A case just created from audio always opens with highlighting off.
  useEffect(() => {
    const reset = () => setRedactionsHighlightEnabled(false);
    window.addEventListener(EDITOR_NEW_CASE_RESET_EVENT, reset);
    return () => window.removeEventListener(EDITOR_NEW_CASE_RESET_EVENT, reset);
  }, []);
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;

    if (redactionsHighlightEnabled) {
      // Turn on: mount the marks, then play the reveal. Adding the
      // "redactions-showing" class after the spans exist applies the keyframe
      // animation to them; it's removed once the animation has run so later
      // edits that rebuild decorations don't replay it.
      dom.classList.remove("redactions-hiding");
      editor.commands.setRedactionsEnabled(true);
      dom.classList.add("redactions-showing");
      const t = setTimeout(
        () => dom.classList.remove("redactions-showing"),
        240,
      );
      return () => clearTimeout(t);
    }

    // Turn off: if marks are currently visible, fade their colors out first,
    // then unmount them. If nothing is showing, just disable immediately.
    dom.classList.remove("redactions-showing");
    if ((editor.storage.redactions?.matchCount ?? 0) > 0) {
      dom.classList.add("redactions-hiding");
      const t = setTimeout(() => {
        editor.commands.setRedactionsEnabled(false);
        dom.classList.remove("redactions-hiding");
      }, 200);
      return () => {
        clearTimeout(t);
        dom.classList.remove("redactions-hiding");
      };
    }
    editor.commands.setRedactionsEnabled(false);
  }, [editor, redactionsHighlightEnabled]);

  const insertAtTop = (editor, content) => {
    // Find the first heading node
    let inserted = false;
    editor.state.doc.descendants((node, pos) => {
      if (!inserted && node.type.name === "heading") {
        // Calculate position after the heading
        const insertPos = pos + node.nodeSize;
        editor
          .chain()
          .focus()
          .insertContentAt(insertPos, `<p>${content}</p>\n`)
          .run();
        inserted = true;
        return false; // Stop traversing
      }
    });

    // If no heading found, insert at start
    if (!inserted) {
      editor.chain().focus().insertContentAt(0, `<p>${content}</p>\n`).run();
    }
  };

  const insertAtCursor = (editor, content) => {
    editor.commands.insertContent(`<p>${content}</p>`);
  };

  const insertAtEnd = (editor, content) => {
    if (editor) {
      // Move cursor to end of document
      editor.commands.setTextSelection(editor.state.doc.content.size);
      // Add newline before new content if there isn't one
      const needsNewline = !editor.state.doc.textContent.endsWith("\n");
      editor.commands.insertContent(
        (needsNewline ? "\n" : "") + `<p>${content}</p>`,
      );
    }
  };

  const handleInsertContent = (
    content: string,
    mode: "cursor" | "top" | "end" = "cursor",
  ) => {
    if (editor) {
      switch (mode) {
        case "top":
          insertAtTop(editor, content);
          break;
        case "end":
          insertAtEnd(editor, content);
          break;
        case "cursor":
        default:
          insertAtCursor(editor, content);
      }
      handleUpdate(editor);
    }
  };

  const SidebarNav = () => {
    // If Records user viewing another case, don't show any tabs
    if (isRecordsViewingOtherCase) {
      return null;
    }

    // Segmented icon tabs — everything fits without horizontal scrolling, and
    // each tool reads as a compact icon + label tile. Checklist only appears
    // when the case actually has one.
    const tabs: {
      id: "suggestions" | "snippets" | "statutes" | "redactions" | "checklists";
      label: string;
      Icon: typeof SparklesIcon;
    }[] = [
      { id: "suggestions", label: "Suggestions", Icon: SparklesIcon },
      { id: "snippets", label: "Snippets", Icon: ClipboardDocumentListIcon },
      { id: "statutes", label: "Statutes", Icon: BookOpenIcon },
      { id: "redactions", label: "Redactions", Icon: EyeSlashIcon },
      ...(checklist
        ? [
            {
              id: "checklists" as const,
              label: "Checklist",
              Icon: CheckCircleIcon,
            },
          ]
        : []),
    ];

    const activeNavTab =
      tabs.find((tab) => tab.id === activeComponent) ?? tabs[0];

    const isCompact = isSidebarCompact;

    const selectTab = (id: (typeof tabs)[number]["id"]) => {
      setActiveComponent(id);
      localStorage.setItem("pnai-editorActiveComponent", id);
    };

    return (
      <div data-tour="editor-sidebar-tabs" className="sticky top-0 z-10 border-b border-base-300 bg-gray-100 dark:bg-base-200 px-2 pb-2 pt-2">
        {/* Smaller viewports: collapsed dropdown — tap the active tool to
            reveal the full vertical list of tools. */}
        <div className="lg:hidden">
          <button
            type="button"
            aria-expanded={isSidebarNavOpen}
            onClick={() => setIsSidebarNavOpen((open) => !open)}
            className="flex w-full items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-sm font-medium text-base-content"
          >
            <activeNavTab.Icon className="size-4 shrink-0 text-blue-600 dark:text-blue-300" />
            <span className="truncate">{activeNavTab.label}</span>
            <ChevronDownIcon
              className={cn(
                "ml-auto size-4 shrink-0 text-base-content/40 transition-transform",
                isSidebarNavOpen && "rotate-180",
              )}
            />
          </button>
          {isSidebarNavOpen && (
            <div className="mt-1 flex flex-col gap-0.5 rounded-lg border border-base-300 bg-base-100 p-1 shadow-sm">
              {tabs.map((tab) => {
                const isActive = activeComponent === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      selectTab(tab.id);
                      setIsSidebarNavOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-blue-50 dark:bg-blue-900/40 font-medium text-blue-600 dark:text-blue-300"
                        : "text-gray-700 dark:text-base-content/80 hover:bg-base-200",
                    )}
                  >
                    <tab.Icon className="size-4 shrink-0" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* lg and up, narrow panel (e.g. 150% browser zoom): a single row of
            icon-only buttons, so no label gets ellipsed. */}
        {isCompact && (
          <div className="hidden gap-1 rounded-lg bg-base-300/70 p-1 lg:flex">
            {tabs.map((tab) => {
              const isActive = activeComponent === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  title={tab.label}
                  aria-label={tab.label}
                  aria-pressed={isActive}
                  onClick={() => selectTab(tab.id)}
                  className={cn(
                    "flex flex-1 select-none items-center justify-center rounded-md py-2 transition-all",
                    isActive
                      ? "bg-base-100 text-blue-600 dark:text-blue-300 shadow-sm"
                      : "text-gray-600 dark:text-base-content/70 hover:bg-base-100/70 hover:text-base-content",
                  )}
                >
                  <tab.Icon className="size-5 shrink-0" />
                </button>
              );
            })}
          </div>
        )}

        {/* lg and up, roomy panel: two-column labelled tiles. */}
        {!isCompact && (
          <div className="hidden grid-cols-2 gap-1.5 lg:grid">
            {tabs.map((tab) => {
              const isActive = activeComponent === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  title={tab.label}
                  aria-pressed={isActive}
                  onClick={() => selectTab(tab.id)}
                  className={cn(
                    "flex min-w-0 select-none items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium leading-tight transition-all",
                    isActive
                      ? "border-blue-200 dark:border-blue-800 bg-base-100 text-blue-600 dark:text-blue-300 shadow-sm"
                      : "border-transparent bg-base-300/70 text-gray-600 dark:text-base-content/70 hover:bg-base-100/70 hover:text-base-content",
                  )}
                >
                  <tab.Icon className="size-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-left">
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <EditorMenuBar
        editor={editor}
        disabled={isReadOnly || isRegeneratingNarrative || isAddingToReport}
        caseId={caseId}
        score={caseScore?.score || null}
        onChange={onChange}
        onTranscribeStateChange={setIsTranscribing}
        activeTab={activeTab}
        findSlotRef={setFindSlot}
        transcriptFindOpen={transcriptFindOpen}
        onToggleTranscriptFind={
          activeTab === "transcription" &&
          !showFootage &&
          !isLoadingTranscription &&
          currentTranscription
            ? () => setTranscriptFindOpen((open) => !open)
            : undefined
        }
        isProcessing={isProcessing}
        caseTranscription={caseTranscription}
        caseAudioMetadata={caseAudioMetadata}
        caseStatus={caseStatus}
        currentTranscription={currentTranscription}
        isAiGenerated={isAiGenerated}
        caseRedactions={caseRedactions}
        onCaseDeleted={onCaseDeleted}
        proofreadEnabled={proofreadEnabled}
        onToggleProofread={toggleProofread}
        placeholdersHidden={placeholdersHidden}
        activeFileId={
          activeTranscriptionType !== "main" &&
          activeTranscriptionType !== "supplemental"
            ? activeTranscriptionType
            : undefined
        }
      />
      <div
        className="flex h-screen relative overflow-hidden"
        style={{ "--sidebar-w": `${sidebarPanel.width}px` } as React.CSSProperties}
      >
        {isTranscribing && (
          <div className="absolute inset-0 bg-base-100/70 z-50 pointer-events-none"></div>
        )}
        <div
          className={cn(
            minimized ? "w-full" : "w-full md:w-[calc(100%-var(--sidebar-w))]",
            "md:border-r-2 bg-base-100 h-full flex flex-col relative",
            !sidebarPanel.isResizing && "transition-all duration-300 ease-in-out",
          )}
        >
          {transcriptionCount > 0 && (
            <div className="w-full flex bg-base-100 px-12 lg:px-24 pt-6 max-w-4xl mx-auto sticky top-12 md:top-0 z-[5]">
              <div className="relative flex-1 narrative-dropdown-container">
                <Button
                  onClick={() => {
                    if (activeTab !== "narrative") {
                      // If not on narrative tab, switch to it
                      setActiveTab("narrative");
                    } else {
                      // If already on narrative tab, toggle dropdown
                      setShowNarrativeDropdown(!showNarrativeDropdown);
                    }
                  }}
                  variant="tab"
                  size="sm"
                  className="w-full flex items-center justify-center gap-2"
                  data-tour="editor-tab-narrative"
                  selected={activeTab === "narrative"}
                >
                  <span>Narrative</span>
                  <div
                    className="flex items-center overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      width: activeTab === "narrative" ? "16px" : "0px",
                      opacity: activeTab === "narrative" ? 1 : 0,
                    }}
                  >
                    <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                  </div>
                </Button>

                {/* Dropdown for narrative actions */}
                {showNarrativeDropdown && activeTab === "narrative" && (
                  <div className="absolute top-full right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg py-2 min-w-[200px] max-h-[400px] overflow-y-auto z-50 narrative-dropdown">
                    <button
                      onClick={() => confirmDialogRef.current?.showModal()}
                      data-tour="regenerate-narrative"
                      disabled={isRegeneratingNarrative}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-base-200 flex items-center gap-3 text-gray-700 dark:text-base-content/80 disabled:cursor-not-allowed"
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div>
                        <div className="font-medium flex items-center gap-2 text-blue-600 dark:text-blue-300">
                          <ArrowPathIcon
                            className={`h-4 w-4 ${isRegeneratingNarrative ? "animate-spin" : ""}`}
                          />
                          <span>
                            {isRegeneratingNarrative
                              ? "Regenerating..."
                              : "Regenerate Narrative"}
                          </span>
                        </div>
                        <div className="text-xs text-base-content/60">
                          Generate a new narrative from transcription
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              <div className="relative flex-1 transcription-dropdown-container">
                <Button
                  onClick={() => {
                    if (hasMultipleTranscriptions) {
                      if (activeTab !== "transcription") {
                        // If not on transcription tab, switch to it first
                        setActiveTab("transcription");
                      } else {
                        // If already on transcription tab, toggle dropdown
                        setShowTranscriptionDropdown(
                          !showTranscriptionDropdown,
                        );
                      }
                    } else {
                      // If only one transcription, just switch tabs
                      setActiveTab("transcription");
                    }
                  }}
                  variant="tab"
                  size="sm"
                  className="w-full flex items-center justify-center gap-2 transcription-button"
                  data-tour="editor-tab-transcription"
                  selected={activeTab === "transcription"}
                >
                  <span>{hasFootage ? "Footage" : "Transcription"}</span>
                  {hasFootage && footageFiles.some((f) => !["completed", "failed"].includes(f.metadata?.video?.status)) && (
                    <span className="inline-block size-2 rounded-full bg-amber-500 animate-pulse" title="Reading the footage" />
                  )}
                  <div
                    className="flex items-center gap-2 overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      width:
                        hasMultipleTranscriptions &&
                        activeTab === "transcription"
                          ? "60px"
                          : "0px",
                      opacity:
                        hasMultipleTranscriptions &&
                        activeTab === "transcription"
                          ? 1
                          : 0,
                    }}
                  >
                    <div className="bg-blue-600 text-white rounded px-1.5 py-0.5 text-xs font-medium min-w-[20px] h-5 flex items-center justify-center whitespace-nowrap">
                      {transcriptionCount}
                    </div>
                    <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                  </div>
                </Button>

                {/* Dropdown for multiple transcriptions */}
                {showTranscriptionDropdown &&
                  hasMultipleTranscriptions &&
                  activeTab === "transcription" && (
                    <div className="absolute top-full right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg py-1 min-w-[240px] max-h-[400px] overflow-y-auto z-50 transcription-dropdown">
                      {caseTranscription && (
                        <button
                          onClick={() => handleTranscriptionTypeChange("main")}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-3 ${
                            activeTranscriptionType === "main"
                              ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                              : "text-gray-700 dark:text-base-content/80"
                          }`}
                        >
                          <MicrophoneIcon
                            className={`size-4 shrink-0 ${caseAudioMetadata?.audioType === "interaction" ? "text-purple-500" : "text-blue-500 dark:text-blue-300"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              Main Recording
                            </div>
                            <div className="text-xs text-base-content/40">
                              {caseAudioMetadata?.audioType === "interaction"
                                ? "Interaction"
                                : "Narrative"}
                            </div>
                          </div>
                          {activeTranscriptionType === "main" && (
                            <CheckCircleIcon className="size-4 text-blue-500 dark:text-blue-300 shrink-0" />
                          )}
                        </button>
                      )}
                      {supplementalTranscription && (
                        <button
                          onClick={() =>
                            handleTranscriptionTypeChange("supplemental")
                          }
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-3 ${
                            activeTranscriptionType === "supplemental"
                              ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                              : "text-gray-700 dark:text-base-content/80"
                          }`}
                        >
                          <MicrophoneIcon
                            className={`size-4 shrink-0 ${caseAudioMetadata?.supplementalAudio?.type === "interaction" ? "text-purple-500" : "text-blue-500 dark:text-blue-300"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              Supplemental Recording
                            </div>
                            <div className="text-xs text-base-content/40">
                              {caseAudioMetadata?.supplementalAudio?.type ===
                              "narrative"
                                ? "Narrative"
                                : "Audio"}
                            </div>
                          </div>
                          {activeTranscriptionType === "supplemental" && (
                            <CheckCircleIcon className="size-4 text-blue-500 dark:text-blue-300 shrink-0" />
                          )}
                        </button>
                      )}
                      {filesWithTranscriptions.map((file) => {
                        const isVideo =
                          file.file_type?.startsWith("video/") ||
                          file.media_type === "video";
                        const isInteraction =
                          file.metadata?.audioType === "interaction";
                        const source = file.metadata?.source;
                        const fileDate = file.created_at
                          ? new Date(file.created_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )
                          : null;

                        // Build a descriptive label
                        let label = fileDate || file.file_name || "Recording";
                        if (source === "dictation")
                          label = `Dictation — ${fileDate || "Recording"}`;
                        else if (source === "suggestions_insert")
                          label = `Added to Report — ${fileDate || "Recording"}`;

                        const typeLabel = isVideo
                          ? "Video"
                          : isInteraction
                            ? "Interaction"
                            : "Narrative";

                        return (
                          <button
                            key={file.id}
                            onClick={() =>
                              handleTranscriptionTypeChange(file.id)
                            }
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-3 ${
                              activeTranscriptionType === file.id
                                ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                                : "text-gray-700 dark:text-base-content/80"
                            }`}
                          >
                            {isVideo ? (
                              <VideoCameraIcon
                                className={`size-4 shrink-0 ${isInteraction ? "text-purple-500" : "text-blue-500 dark:text-blue-300"}`}
                              />
                            ) : (
                              <MicrophoneIcon
                                className={`size-4 shrink-0 ${isInteraction ? "text-purple-500" : "text-blue-500 dark:text-blue-300"}`}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {label}
                              </div>
                              <div className="text-xs text-base-content/40">
                                {typeLabel}
                                {file.file_size ? ` · ${file.file_size}` : ""}
                              </div>
                            </div>
                            {activeTranscriptionType === file.id && (
                              <CheckCircleIcon className="size-4 text-blue-500 dark:text-blue-300 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
              </div>
              {!isReadOnly && activeTab === "transcription" && !showFootage && (
                <button
                  onClick={() => setIsFollowing(!isFollowing)}
                  className={`absolute right-0 top-24 mr-4 w-12 h-12 rounded-full transition-all duration-100 z-40 flex items-center justify-center ${
                    isFollowing
                      ? "bg-blue-500 hover:bg-blue-600"
                      : "bg-gray-100 dark:bg-base-200 hover:bg-base-300"
                  }`}
                  title={isFollowing ? "Stop following" : "Follow audio"}
                  data-tour="transcript-follow"
                >
                  <ArrowDownIcon
                    className={`w-6 h-6 ${
                      isFollowing ? "text-white" : "text-blue-500 dark:text-blue-300"
                    }`}
                  />
                </button>
              )}
            </div>
          )}
          {hasFootage && activeTab === "transcription" && (
            <div className="w-full flex justify-center bg-base-100 px-12 lg:px-24 pt-3 max-w-4xl mx-auto">
              <div className="inline-flex rounded-lg border border-base-300 overflow-hidden text-xs" role="radiogroup" aria-label="Footage or transcription">
                {(["footage", "transcription"] as const).map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={footageView === v}
                    onClick={() => setFootageView(v)}
                    className={cn("px-3 py-1.5 capitalize transition-colors", i > 0 && "border-l border-base-300", footageView === v ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-medium" : "text-base-content/70 hover:bg-base-200")}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showFootage && (
            <div className="flex-1 min-h-0 bg-base-100 border-t border-base-300 mt-3">
              <FootagePanel
                caseId={caseId!}
                files={footageFiles}
                initialSeekMs={footageOpenAtMs}
                onTime={(fileId, ms) => {
                  footageTime.current = { fileId, ms };
                }}
                onClose={() => setFootageView("transcription")}
                onOpenSuggestions={() => {
                  setActiveComponent("suggestions");
                  localStorage.setItem("pnai-editorActiveComponent", "suggestions");
                }}
              />
            </div>
          )}
          <div className={cn("flex-1 bg-base-100 flex justify-center overflow-y-auto", showFootage && "hidden")}>
            <div
              className={cn(
                "flex-1 pt-0 pb-6 px-12 md:px-24 max-w-4xl w-full",
                activeTab === "transcription" && "pt-0",
              )}
            >
              <div className="flex-grow">
                <div className={activeTab === "narrative" ? "block" : "hidden"}>
                  <div data-tour="editor-narrative" className="editor [&_.ProseMirror_ul_>_li_>_p]:mb-0 [&_.ProseMirror_ul_>_li_>_p]:mt-0 [&_.ProseMirror_ol_>_li_>_p]:mb-0 [&_.ProseMirror_ol_>_li_>_p]:mt-0">
                    {editor && (
                      <FloatingMenu
                        editor={editor}
                        onChange={onChange}
                        caseId={caseId}
                        caseRedactions={caseRedactions}
                        setCaseRedactions={setCaseRedactions}
                        onEnableRedactionHighlight={() =>
                          setRedactionsHighlightEnabled(true)
                        }
                        onOpenRedactionsTab={() => {
                          setActiveComponent("redactions");
                          localStorage.setItem(
                            "pnai-editorActiveComponent",
                            "redactions",
                          );
                          // Un-minimize the sidebar so the list is visible.
                          setMinimized(false);
                          localStorage.setItem(
                            "pnai-editorMinimized",
                            JSON.stringify(false),
                          );
                        }}
                        onAiGenerated={() => setIsAiGenerated?.(true)}
                        onAiReverted={() => {
                          // Re-check server — other AI audits (first_draft, etc.) may still exist
                          if (caseId) {
                            axios
                              .get(`/api/audits/${caseId}/ai-generated`, {
                                withCredentials: true,
                              })
                              .then((res) =>
                                setIsAiGenerated?.(res.data.aiGenerated),
                              )
                              .catch(() => {});
                          }
                        }}
                      />
                    )}
                    <ProofreadPopover editor={editor} />
                    {isCaseLoading && <SkeletonEditorContent />}
                    {/* Hidden, not unmounted, while the case reloads (Add to
                        Report's onRecordComplete refetches it). Unmounting
                        moves the editor into a new element, and the bubble
                        menu's tippy, created once against the old one, would
                        never show again until a page refresh. */}
                    <EditorContent
                      editor={editor}
                      className={cn(
                        "max-w-none mt-8 [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:text-base-content",
                        "[&_.ProseMirror_h1]:mt-6 [&_.ProseMirror_h1]:mb-6",
                        "[&_.ProseMirror_h2]:mt-6 [&_.ProseMirror_h2]:mb-0",
                        "[&_.ProseMirror_h3]:mt-6 [&_.ProseMirror_h3]:mb-0",
                        "[&_.ProseMirror_p]:mt-4 [&_.ProseMirror_p]:mb-0",
                        isRegeneratingNarrative &&
                          "opacity-50 pointer-events-none",
                        isCaseLoading && "hidden",
                      )}
                    />
                    {showDisclaimer && (
                      <div className="mt-6 pt-4 pb-32 border-t border-base-300 select-none pointer-events-none">
                        <p className="text-xs text-base-content/40 mb-1 font-medium uppercase tracking-wide">
                          California SB 524
                        </p>
                        <p className="text-sm text-base-content/60 italic">
                          This report was written either fully or in part using
                          artificial intelligence. I affirm that this report,
                          generated with the assistance of Police Narratives AI,
                          has been thoroughly reviewed and edited by me to
                          ensure its accuracy in representing my recollection of
                          the reported events. I acknowledge the potential legal
                          implications of this statement and, if required, am
                          prepared to testify to its accuracy.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={
                    activeTab === "transcription" ? "block h-full" : "hidden"
                  }
                >
                  {/* Transcription Tab Content */}
                  {activeTab === "transcription" && isLoadingTranscription && (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-sm text-base-content/60">
                          Loading transcription...
                        </p>
                      </div>
                    </div>
                  )}
                  {activeTab === "transcription" &&
                    !isLoadingTranscription &&
                    currentTranscription && (
                      <>
                        {/* Transcription component - key forces remount on case change to reset internal state */}
                        <Transcription
                          key={caseId}
                          transcription={currentTranscription}
                          onWordClick={handleWordClick}
                          currentTime={currentTime}
                          caseAudioMetadata={currentAudioMetadata}
                          setCaseAudioMetadata={
                            activeTranscriptionType !== "main" &&
                            activeTranscriptionType !== "supplemental"
                              ? (updated: any) => {
                                  setCaseFiles?.((prev) =>
                                    prev.map((f) =>
                                      f.id === activeTranscriptionType
                                        ? { ...f, metadata: updated }
                                        : f,
                                    ),
                                  );
                                }
                              : setCaseAudioMetadata
                          }
                          isFollowing={isFollowing}
                          setIsFollowing={setIsFollowing}
                          caseId={caseId}
                          fileId={
                            activeTranscriptionType !== "main" &&
                            activeTranscriptionType !== "supplemental"
                              ? activeTranscriptionType
                              : undefined
                          }
                          onRegenerateStart={() =>
                            setIsRegeneratingNarrative(true)
                          }
                          onRegenerateEnd={() =>
                            setIsRegeneratingNarrative(false)
                          }
                          handleSelectedCase={handleSelectedCase}
                          isReadOnly={isReadOnly}
                          setCaseTranscription={
                            activeTranscriptionType !== "main" &&
                            activeTranscriptionType !== "supplemental"
                              ? (updated: any) =>
                                  setFileTranscriptions((prev) => ({
                                    ...prev,
                                    [activeTranscriptionType]: updated,
                                  }))
                              : setCaseTranscription
                          }
                          caseFiles={caseFiles}
                          findDisabled={showFootage}
                          findPortal={findSlot}
                          findOpen={transcriptFindOpen}
                          setFindOpen={setTranscriptFindOpen}
                        />
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
          {showAddToReportOverlay && (
            <div className="absolute inset-0 z-40 bg-base-100/80 backdrop-blur-sm flex items-center justify-center animate-fade-in">
              <div className="text-center max-w-md mx-auto p-8">
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-ping"></div>
                  <div
                    className="absolute inset-2 rounded-full bg-blue-400/40 animate-ping"
                    style={{ animationDelay: "300ms" }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/30 flex items-center justify-center">
                      <SparklesIcon className="w-8 h-8 text-white animate-pulse" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-base-content mb-2">
                  {isUploadingAddToReport
                    ? "Uploading audio"
                    : "Generating changes"}
                </h3>
                <p className="text-sm text-gray-600 dark:text-base-content/70 mb-3">
                  {isUploadingAddToReport
                    ? "Your audio is being uploaded. Please keep this page open until the upload finishes."
                    : "Adding new details to your report. This usually takes 1-3 minutes."}
                </p>
                {isUploadingAddToReport ? (
                  <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg p-3 mb-5">
                    <p className="text-xs text-amber-800 dark:text-amber-400 font-medium">
                      Do not close or navigate away while files are uploading.
                    </p>
                  </div>
                ) : (
                  <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg p-3 mb-5">
                    <p className="text-xs text-green-800 dark:text-green-400 font-medium">
                      You can safely leave this page. Processing will continue
                      in the background.
                    </p>
                  </div>
                )}
                <div className="w-64 mx-auto h-1 bg-base-300 rounded-full overflow-hidden mb-5">
                  <div className="h-full bg-gradient-to-r from-blue-200 via-blue-500 to-blue-200 bg-[length:200%_100%] animate-shimmer"></div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!caseId || isCancellingAddToReport) return;
                    if (
                      !window.confirm(
                        "Cancel Add to Report? Uploaded files will be deleted and the editor will be unlocked.",
                      )
                    ) {
                      return;
                    }
                    setIsCancellingAddToReport(true);
                    try {
                      // Abort the in-flight upload pipeline first. This
                      // interrupts an in-progress S3 upload (so the
                      // beforeunload listener is removed and the user doesn't
                      // get an "are you sure you want to leave" prompt on
                      // refresh) and prevents the audioMetadata PATCH from
                      // re-setting the intent we're about to clear.
                      abortCaseUpload(caseId);
                      await axios.post(
                        `/api/cases/${caseId}/cancel-add-to-report`,
                        {},
                        { withCredentials: true },
                      );
                      // Dismiss the upload progress toast that
                      // audioProcessingService is still showing for this case.
                      // It's not tied to the case state, so the cancel endpoint
                      // alone wouldn't make it go away.
                      dismissCaseToast(caseId);
                      // Local state — flips overlay off immediately for the
                      // current session.
                      setIsAddingToReport(false);
                      setIsUploadingAddToReport(false);
                      // Refresh the case so persistedIntent / pending blob /
                      // case_files all reflect the cleanup.
                      handleSelectedCase(caseId);
                    } catch (err) {
                      console.error("Failed to cancel Add to Report:", err);
                      // eslint-disable-next-line no-alert
                      window.alert(
                        "Failed to cancel. Please try again or refresh the page.",
                      );
                    } finally {
                      setIsCancellingAddToReport(false);
                    }
                  }}
                  disabled={isCancellingAddToReport || !caseId}
                  className="text-sm font-medium text-gray-600 dark:text-base-content/70 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed underline-offset-4 hover:underline transition-colors"
                >
                  {isCancellingAddToReport ? "Cancelling..." : "Cancel"}
                </button>
              </div>
            </div>
          )}
        </div>
        {(canEditNarrative ||
          isRecordsViewingOtherCase ||
          activeTab === "transcription" ||
          (isRecords() && activeTab === "narrative")) && (
          <>
            <div
              className={cn(
                "flex absolute z-20 h-48 cursor-pointer",
                !sidebarPanel.isResizing && "transition-all duration-300 ease-in-out",
                minimized
                  ? "right-[-20px] hover:right-0"
                  : "right-0 md:right-[var(--sidebar-w)]",
                "top-8",
              )}
              onClick={() => {
                const newValue = !minimized;
                setMinimized(newValue);
                localStorage.setItem(
                  "pnai-editorMinimized",
                  JSON.stringify(newValue),
                );
              }}
            >
              <div
                className={cn(
                  "absolute right-3 rounded-l-3xl hover:rounded-full top-10 md:top-[-12px] bottom-0 z-50 bg-gray-100 dark:bg-base-200 h-12 w-12 corner transition-all duration-250 ease-in-out",
                  !minimized &&
                    "!rounded-full border-2 md:border-0 border-base-300",
                )}
              >
                {minimized ? (
                  isRecordsViewingOtherCase ? (
                    <SparklesIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[9px] mt-3" />
                  ) : activeComponent === "suggestions" ? (
                    <SparklesIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[9px] mt-3" />
                  ) : activeComponent === "snippets" ? (
                    <ClipboardDocumentListIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[9px] mt-3" />
                  ) : activeComponent === "redactions" ? (
                    <EyeSlashIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[9px] mt-3" />
                  ) : activeComponent === "checklists" ? (
                    <CheckCircleIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[9px] mt-3" />
                  ) : (
                    <BookOpenIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[12px] mt-[12px]" />
                  )
                ) : (
                  <ChevronRightIcon className="size-6 text-blue-500 dark:text-blue-300 ml-[12px] mt-[12px]" />
                )}
              </div>
            </div>
            <div
              ref={sidebarRef}
              className={cn(
                "mt-16 md:mt-0",
                !sidebarPanel.isResizing &&
                  "transition-opacity md:transition-all md:duration-300 md:ease-in-out",
                "absolute md:relative bg-gray-100 dark:bg-base-200 h-screen flex flex-col z-10",
                minimized
                  ? "w-0 opacity-0 md:invisible"
                  : "w-full md:w-[var(--sidebar-w)] opacity-100 md:visible",
              )}
            >
              {!minimized && (
                <ResizeHandle edge="left" active={sidebarPanel.isResizing} {...sidebarPanel.handleProps} />
              )}
              {/* MediaPlayer shows current audio based on active transcription type.
                  With footage it plays the proxy video as a mini player and
                  collapses while the footage view has the big player. */}
              {(currentAudioUrl || sidebarVideoUrl) && (
                <div className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", showFootage ? "grid-rows-[0fr]" : "grid-rows-[1fr]")}>
                  <div className={cn("min-h-0", showFootage && "overflow-hidden")}>
                    <MediaPlayer
                      key={`sidebar-media-${activeTranscriptionType}-${sidebarVideoUrl ? "video" : "audio"}`}
                      url={currentAudioUrl || sidebarFootageFile?.file_url || ""}
                      videoUrl={sidebarVideoUrl}
                      onTimeUpdate={setCurrentTime}
                      audioRef={audioPlayerRef}
                      audioMetadata={currentAudioMetadata}
                      setAudioControlsRef={setAudioControlsRef}
                    />
                  </div>
                </div>
              )}
              <SidebarNav />
              {/* min-w-0 is load-bearing: as a flex item this defaults to
                  min-width:auto, so any unbreakable child (score card, primary
                  buttons) pushed the whole scroll area wider than the panel and
                  spilled off the right edge of the screen at 150% zoom. Padding
                  also tightens once the panel is too narrow for labelled tabs. */}
              <div
                className={cn(
                  "py-0 md:py-4 flex-1 min-h-0 min-w-0 overflow-y-auto",
                  isSidebarCompact ? "px-2" : "px-4",
                )}
              >
                {isRecordsViewingOtherCase ? (
                  // Records users viewing other cases see the record score
                  // with the redaction tools stacked underneath (no tab nav)
                  // so they can review/redact before releasing records.
                  <div className="p-2">
                    <h3 className="text-lg font-semibold text-base-content mb-4">
                      Record Information
                    </h3>
                    <ReportScore score={caseScore} isLoading={isCaseLoading} />
                    <div className="mt-6">
                      <Redactions
                        caseRedactions={caseRedactions}
                        setCaseRedactions={setCaseRedactions}
                        caseId={caseId}
                        editor={editor}
                        editorContent={editor?.getText()}
                        isCaseLoading={isCaseLoading}
                        highlightEnabled={redactionsHighlightEnabled}
                        setHighlightEnabled={setRedactionsHighlightEnabled}
                      />
                    </div>
                  </div>
                ) : (
                  // Normal sidebar content for case owners and officers
                  <>
                    {activeComponent === "suggestions" && openPolicySection && (
                      <PolicySectionView
                        reference={openPolicySection}
                        onBack={() => setOpenPolicySection(null)}
                      />
                    )}
                    {activeComponent === "suggestions" && (
                      // Kept mounted while a section is open so an evaluation
                      // in progress and its state survive; contents = no box.
                      <div className={openPolicySection ? "hidden" : "contents"}>
                      <Suggestions
                        caseSuggestions={caseSuggestions}
                        setCaseSuggestions={setCaseSuggestions}
                        caseScore={caseScore}
                        setCaseScore={setCaseScore}
                        caseId={caseId}
                        isCaseLoading={isCaseLoading}
                        editorContent={editor
                          ?.getText()
                          .replace(/\n{3,}/g, "\n\n")
                          .trim()}
                        onRecordComplete={() =>
                          caseId && handleSelectedCase(caseId)
                        }
                        onSaveEditor={() => editor && handleUpdate(editor)}
                        isAddingToReport={isAddingToReport}
                        setIsAddingToReport={setIsAddingToReport}
                        setIsUploadingAddToReport={setIsUploadingAddToReport}
                        isAddToReportInFlight={isAddToReportInFlight}
                        editor={editor}
                        pendingSuggestionsChanges={pendingSuggestionsChanges}
                        onPendingChangesCleared={onPendingChangesCleared}
                        setIsReviewingChanges={setIsReviewingChanges}
                        policyReferences={casePolicyReferences}
                        onOpenPolicy={openPolicyFromBadge}
                        placeholdersHidden={placeholdersHidden}
                        onTogglePlaceholdersHidden={togglePlaceholdersHidden}
                      />
                      </div>
                    )}
                    {activeComponent === "snippets" && (
                      <Snippets onInsertContent={handleInsertContent} />
                    )}
                    {activeComponent === "statutes" && (
                      <Statutes
                        caseStatutes={caseStatutes}
                        setCaseStatutes={setCaseStatutes}
                        onInsertContent={handleInsertContent}
                        caseId={caseId}
                        onUpdateStatutes={handleUpdateStatutes}
                        editorContent={editor?.getText()}
                        isCaseLoading={isCaseLoading}
                      />
                    )}
                    {activeComponent === "redactions" && (
                      <Redactions
                        caseRedactions={caseRedactions}
                        setCaseRedactions={setCaseRedactions}
                        caseId={caseId}
                        editor={editor}
                        editorContent={editor?.getText()}
                        isCaseLoading={isCaseLoading}
                        highlightEnabled={redactionsHighlightEnabled}
                        setHighlightEnabled={setRedactionsHighlightEnabled}
                      />
                    )}
                    {activeComponent === "checklists" && checklist && (
                      <Checklists
                        checklist={checklist}
                        onChange={(updatedData) => {
                          if (!caseId) return;

                          // Clear existing debounce timer
                          if (checklistDebounceTimerRef.current) {
                            clearTimeout(checklistDebounceTimerRef.current);
                          }

                          // Set new debounce timer
                          checklistDebounceTimerRef.current = setTimeout(
                            async () => {
                              try {
                                await axios.patch(
                                  `/api/cases/${caseId}/checklist`,
                                  {
                                    checklist_data: updatedData,
                                  },
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to save checklist progress",
                                  e,
                                );
                              }
                            },
                            500,
                          );
                        }}
                        isReadOnly={isReadOnly}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation dialog for regenerating narrative */}
      <dialog ref={confirmDialogRef} className="modal" data-tour="regenerate-dialog">
        <div className="modal-box prose dark:prose-invert w-11/12 md:w-5/12 p-8">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Confirm Narrative Regeneration
          </h3>
          <p className="text-gray-600 dark:text-base-content/70 mb-6">
            Are you sure you want to regenerate the narrative from this
            transcription? This will completely replace the current narrative
            content and you will lose all manual edits that have been made to
            the narrative.
          </p>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => confirmDialogRef.current?.close()}
              disabled={isRegeneratingNarrative}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateNarrative}
              data-tour="regenerate-confirm"
              loading={isRegeneratingNarrative}
              disabled={isRegeneratingNarrative}
            >
              Confirm
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => confirmDialogRef.current?.close()}>
            close
          </button>
        </form>
      </dialog>
    </>
  );
};

interface EditorProps {
  caseContent?: { data: string };
  onChange?: ({
    content,
    caseId,
    statutes,
  }: {
    content: { data: string };
    caseId?: string;
    statutes?: StatuteType[];
  }) => void;
  caseId?: string;
  hasCases?: boolean;
  page: string;
  caseSuggestions?: Suggestion[];
  caseScore?: CaseScore;
  caseAudioUrl?: string;
  caseStatutes?: StatuteType[];
  caseRedactions?: RedactionType[];
  casePolicyReferences?: PolicyReference[];
  isProcessing?: boolean;
  isCaseLoading?: boolean;
  setCaseStatutes?: React.Dispatch<React.SetStateAction<StatuteType[]>>;
  setCaseRedactions?: React.Dispatch<React.SetStateAction<RedactionType[]>>;
  setCaseSuggestions?: React.Dispatch<React.SetStateAction<Suggestion[]>>;
  setCaseScore: React.Dispatch<React.SetStateAction<CaseScore | undefined>>;
  caseTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setCaseTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  supplementalTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setSupplementalTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  activeTab: "narrative" | "transcription";
  setActiveTab: React.Dispatch<
    React.SetStateAction<"narrative" | "transcription">
  >;
  caseAudioMetadata?: AudioMetadataType | null;
  setCaseAudioMetadata?: React.Dispatch<
    React.SetStateAction<AudioMetadataType | null>
  >;
  pendingSuggestionsChanges?: PendingSuggestionsChanges | null;
  onPendingChangesCleared?: () => void;
  handleSelectedCase: (caseId: string) => void;
  canEditCase?: boolean;
  caseOwnerId?: string;
  setAudioControlsRef?: React.Dispatch<
    React.SetStateAction<{
      rewind: (seconds: number) => void;
      fastForward: (seconds: number) => void;
      play: () => void;
      pause: () => void;
    } | null>
  >;
  caseStatus: (typeof CaseStatus.enumValues)[number];
  checklist: {
    template: ChecklistTemplate;
    data: ChecklistData;
    completed_at?: string;
    created_at?: string;
    updated_at?: string;
  } | null;
  setCaseContent?: React.Dispatch<
    React.SetStateAction<{ data: string } | undefined>
  >;
  caseFiles?: any[];
  setCaseFiles?: React.Dispatch<React.SetStateAction<any[]>>;
  isAiGenerated?: boolean;
  setIsAiGenerated?: React.Dispatch<React.SetStateAction<boolean>>;
  onCaseDeleted?: (caseId: string) => void;
}

function Editor({
  caseContent,
  onChange,
  hasCases,
  caseId,
  page,
  caseSuggestions,
  caseScore,
  caseStatutes,
  caseRedactions,
  setCaseRedactions,
  casePolicyReferences,
  caseAudioUrl,
  caseTranscription,
  isProcessing,
  isCaseLoading,
  setCaseStatutes,
  setCaseSuggestions,
  setCaseScore,
  setCaseTranscription,
  supplementalTranscription,
  setSupplementalTranscription,
  activeTab,
  setActiveTab,
  caseAudioMetadata,
  setCaseAudioMetadata,
  pendingSuggestionsChanges,
  onPendingChangesCleared,
  handleSelectedCase,
  canEditCase = true,
  caseOwnerId,
  setAudioControlsRef,
  caseStatus,
  checklist,
  setCaseContent,
  caseFiles,
  setCaseFiles,
  isAiGenerated,
  setIsAiGenerated,
  onCaseDeleted,
}: EditorProps) {
  const { isRecords } = useUserRoles();
  const { user } = useAuth();
  const initialContent = caseContent?.data || "";
  const checklistDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Records users can edit their own cases (both narrative and transcription)
  // but can only edit transcriptions on other cases
  // Trashed cases are never editable
  const isDiscarded = page === "trash" || caseStatus === "discarded";
  const canEditNarrative =
    !isDiscarded && canEditCase && (!isRecords() || caseOwnerId === user?.id);
  const isReadOnly = isDiscarded || isProcessing || !canEditCase;

  // Show processing view when case is being processed (initial audio upload)
  const isUploadingFiles =
    caseContent?.data === "Uploading files — please keep this page open...";
  const isTransferringBodyCam =
    caseContent?.data === "Transferring body cam footage...";
  const isProcessingAudio =
    caseContent?.data === "Processing audio..." ||
    caseContent?.data === "Processing audio files...";
  const isInitialProcessing =
    isUploadingFiles || isTransferringBodyCam || isProcessingAudio;
  // Case created via Investigations "Draft report" (export-to-case)
  const isDraftingFromInvestigation = !!caseContent?.data?.includes(
    "Drafting report from investigation..."
  );

  if (hasCases && isProcessing && isInitialProcessing) {
    const isInteractionAudio =
      caseAudioMetadata?.audioType === "interaction" ||
      caseAudioMetadata?.audioType === "mixed";
    const timeEstimate = isInteractionAudio ? "3-7 minutes" : "1-3 minutes";

    // Uploading phase: user must stay on page
    if (isUploadingFiles) {
      return (
        <div data-tour="editor-processing" className="flex items-center justify-center h-screen bg-base-200">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-base-content mb-2">
              Uploading Files
            </h3>
            <p className="text-sm text-base-content/60 mb-4">
              Your files are being uploaded. Please keep this page open until
              uploading is complete.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
              <p className="text-xs text-amber-800 dark:text-amber-400 font-medium">
                Do not close or navigate away from this page while files are
                uploading.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Axon transfer phase: user can leave
    if (isTransferringBodyCam) {
      return (
        <div data-tour="editor-processing" className="flex items-center justify-center h-screen bg-base-200">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-base-content mb-2">
              Transferring Body Cam Footage
            </h3>
            <p className="text-sm text-base-content/60 mb-4">
              Body cam footage is being transferred from Axon. This happens
              server-side, so you're free to edit other cases or navigate away.
            </p>
            <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg p-3">
              <p className="text-xs text-green-800 dark:text-green-400 font-medium">
                You can safely leave this page. Processing will continue in the
                background.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Processing phase: audio uploaded, generating narrative — user can leave
    return (
      <div data-tour="editor-processing" className="flex items-center justify-center h-screen bg-base-200">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-base-content mb-2">
            Processing Audio
          </h3>
          <p className="text-sm text-base-content/60 mb-4">
            We're transcribing your audio and generating the narrative. This
            usually takes {timeEstimate}.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-4 text-left">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>What's happening:</strong>
            </p>
            <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1">
              {isInteractionAudio && (
                <li className="opacity-0 animate-fade-in [animation-fill-mode:forwards]">
                  • Identifying and labeling speakers
                </li>
              )}
              <li className="opacity-0 animate-fade-in [animation-delay:0.25s] [animation-fill-mode:forwards]">
                • Converting audio to text
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:0.75s] [animation-fill-mode:forwards]">
                • Analyzing content for accuracy
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1.25s] [animation-fill-mode:forwards]">
                • Generating police narrative
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1.75s] [animation-fill-mode:forwards]">
                • Creating suggestions and scoring
              </li>
            </ul>
          </div>
          {/* Footage analysis, when the upload asked for it: pass and percentage, live. */}
          <FootageProgress caseId={caseId} className="mt-4" />
          <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg p-3 mt-4">
            <p className="text-xs text-green-800 dark:text-green-400 font-medium">
              You can safely leave this page. Processing will continue in the
              background.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Drafting a case report from an investigation (export-to-case)
  if (hasCases && isProcessing && isDraftingFromInvestigation) {
    return (
      <div className="flex items-center justify-center h-screen bg-base-200">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-base-content mb-2">
            Drafting Investigation Report
          </h3>
          <p className="text-sm text-base-content/60 mb-4">
            We're composing a case narrative from your investigation's brief,
            verified leads, and call and document summaries. This usually takes
            1-2 minutes.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-4 text-left">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>What's happening:</strong>
            </p>
            <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1">
              <li className="opacity-0 animate-fade-in [animation-fill-mode:forwards]">
                • Reviewing the investigation brief and confirmed leads
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:0.5s] [animation-fill-mode:forwards]">
                • Incorporating call and document summaries
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1s] [animation-fill-mode:forwards]">
                • Applying your report preferences
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1.5s] [animation-fill-mode:forwards]">
                • Composing and formatting the narrative
              </li>
            </ul>
          </div>
          <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg p-3 mt-4">
            <p className="text-xs text-green-800 dark:text-green-400 font-medium">
              You can safely leave this page. Drafting will continue in the
              background.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show regenerating narrative view when case is being regenerated (not initial upload)
  if (hasCases && isProcessing && !isInitialProcessing) {
    return (
      <div data-tour="editor-processing" className="flex items-center justify-center h-screen bg-base-200">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-base-content mb-2">
            Regenerating Narrative
          </h3>
          <p className="text-sm text-base-content/60 mb-4">
            We're analyzing your transcription and regenerating the narrative
            with your preferences. This usually takes 1-2 minutes. You're free
            to edit other cases or navigate away from this page.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-4 text-left">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>What's happening:</strong>
            </p>
            <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1">
              <li className="opacity-0 animate-fade-in [animation-fill-mode:forwards]">
                • Analyzing transcription content
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:0.5s] [animation-fill-mode:forwards]">
                • Applying your report preferences
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1s] [animation-fill-mode:forwards]">
                • Generating improved narrative
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1.5s] [animation-fill-mode:forwards]">
                • Formatting and finalizing content
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Render full-screen checklist when status is "checklist" OR checklist exists and is not completed
  if (
    hasCases &&
    (caseStatus === CaseStatusType.enum.checklist ||
      (checklist && !checklist.completed_at))
  ) {
    const caseIdSafe = caseId || "";
    const handleChecklistChange = (updatedData: ChecklistData) => {
      if (!caseIdSafe) return;

      // Clear existing debounce timer
      if (checklistDebounceTimerRef.current) {
        clearTimeout(checklistDebounceTimerRef.current);
      }

      // Set new debounce timer
      checklistDebounceTimerRef.current = setTimeout(async () => {
        try {
          await axios.patch(`/api/cases/${caseIdSafe}/checklist`, {
            checklist_data: updatedData,
          });
        } catch (e) {
          console.error("Failed to save checklist progress", e);
        }
      }, 500);
    };

    const handleChecklistComplete = async () => {
      if (!caseIdSafe) return;
      try {
        await axios.patch(`/api/cases/${caseIdSafe}/checklist`, {
          completed: true,
        });

        // Navigation is handled by audioProcessingService after upload completes
        // Removing handleSelectedCase to prevent re-rendering of checklist view
      } catch (e) {
        console.error("Failed to complete checklist", e);
      }
    };

    return (
      <div className="flex-1 flex flex-col overflow-auto bg-base-200">
        {checklist && (
          <ChecklistView
            key={caseId}
            template={checklist.template}
            data={checklist.data}
            onChange={handleChecklistChange}
            onComplete={handleChecklistComplete}
            isReadOnly={false}
            createdAt={checklist.created_at}
            updatedAt={checklist.updated_at}
            caseId={caseId}
            setCaseContent={setCaseContent}
            handleSelectedCase={handleSelectedCase}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {hasCases ? (
        <TiptapEditor
          key={caseId}
          onChange={onChange || (() => {})}
          caseId={caseId}
          initialContent={initialContent}
          caseSuggestions={caseSuggestions}
          caseScore={caseScore}
          caseAudioUrl={caseAudioUrl}
          isReadOnly={
            activeTab === "narrative" ? !canEditNarrative : isReadOnly
          }
          caseStatutes={caseStatutes}
          setCaseStatutes={setCaseStatutes}
          caseRedactions={caseRedactions}
          setCaseRedactions={setCaseRedactions}
          casePolicyReferences={casePolicyReferences}
          isCaseLoading={isCaseLoading}
          setCaseSuggestions={setCaseSuggestions}
          setCaseScore={setCaseScore}
          caseTranscription={caseTranscription}
          setCaseTranscription={setCaseTranscription}
          supplementalTranscription={supplementalTranscription}
          setSupplementalTranscription={setSupplementalTranscription}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          caseAudioMetadata={caseAudioMetadata}
          setCaseAudioMetadata={setCaseAudioMetadata}
          pendingSuggestionsChanges={pendingSuggestionsChanges}
          onPendingChangesCleared={onPendingChangesCleared}
          isProcessing={isProcessing}
          handleSelectedCase={handleSelectedCase}
          canEditNarrative={canEditNarrative}
          caseOwnerId={caseOwnerId}
          setAudioControlsRef={setAudioControlsRef}
          checklist={checklist}
          caseFiles={caseFiles}
          setCaseFiles={setCaseFiles}
          caseStatus={caseStatus}
          isAiGenerated={isAiGenerated}
          setIsAiGenerated={setIsAiGenerated}
          onCaseDeleted={onCaseDeleted}
        />
      ) : (
        <div className="flex items-center justify-center h-screen bg-base-200">
          <div className="text-center">
            <h3 className="mt-2 text-sm font-semibold text-base-content">
              No cases
            </h3>
            <p className="mt-1 text-sm text-base-content/60">
              Get started by creating a new case.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default Editor;
